/**
 * Information panel about the Scratchpad
 */
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";

import type {Author} from "#content-library/types/types.ts";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import withBackboneModels from "#react-components-package/with-backbone-models.tsx";
import type {Scratchpad} from "#scratchpad-base-package/scratchpads.ts";
import type {ProfileModelType} from "#shared-package/profile-model.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";
import AuthorList from "#tutorial-shared-package/components/author-list.tsx";

import ScratchpadButtons from "./scratchpad-buttons.tsx";

type TimeAgo = typeof import("../react-components-package/timeago.tsx").default;

type ScratchpadButtonsProps = JSX.LibraryManagedAttributes<
    typeof ScratchpadButtons,
    React.ComponentProps<typeof ScratchpadButtons>
>;

type Props = ScratchpadButtonsProps & {
    // The profile object of the person who made this program. This field is
    // used to render author information for custom user programs.
    creatorProfile?: ProfileModelType;
    // A list of authors of the Program. This field only exists for officially
    // published Programs and is used to render author details.
    authorList?: Array<Author>;
    // The origin scratchpad Backbone model
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    originScratchpad: Scratchpad | null | undefined;
    // ISOString
    lastUpdatedTime: string;
} & WithUserProfileProps;

type State = {
    TimeAgo: TimeAgo | null | undefined;
};

class ScratchpadAbout extends React.Component<Props, State> {
    _isMounted: boolean;

    constructor(props: Props) {
        super(props);

        // TODO(briangenisio): This is a hack to remove the deprecated call to
        // this.isMounted() but is still considered an anti-pattern.  Replace
        // with code which does not rely on the mounted state like Redux or GraphQL
        this._isMounted = false;
    }

    state: State = {
        TimeAgo: null,
    };

    componentDidMount() {
        this._isMounted = true;

        import("../react-components-package/timeago.tsx")
            .then((module) => module.default)
            .then((TimeAgo) => {
                if (!this._isMounted) {
                    return;
                }
                this.setState({TimeAgo});
            });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    renderDescription(): React.ReactNode {
        // We render descriptions in the About tab for Talkthroughs & Challenges
        // (We render them above for Projects and Documentation)
        // sync-start:program-description-render 319670203 services/static/apps/khanacademy/src/scratchpads-package/scratchpad-content.tsx
        if (
            this.props.scratchpad.get("contentKind") !== "Talkthrough" &&
            this.props.scratchpad.get("contentKind") !== "Challenge"
        ) {
            return null;
        }
        // sync-end:program-description-render

        const description = this.props.scratchpad.get("translatedDescription");
        if (!description) {
            return null;
        }

        return (
            <div
                className={css(styles.description)}
                dangerouslySetInnerHTML={{
                    __html: description,
                }}
            />
        );
    }

    renderButtons(): React.ReactNode {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        const {
            authorList,
            originScratchpad,
            lastUpdatedTime,
            profile,
            profileLoading,
            profileError,
            ...restProps
        } = this.props;
        /* eslint-enable @typescript-eslint/no-unused-vars */

        return <ScratchpadButtons {...restProps} />;
    }

    renderSpinOffParentDetails(): React.ReactNode {
        const originScratchpad = this.props.originScratchpad;

        if (!originScratchpad || this.props.embedded) {
            return null;
        }

        let title = (
            <Link href={originScratchpad.get("url")}>
                {originScratchpad.get("translatedTitle")}
            </Link>
        );

        if (originScratchpad.get("deleted")) {
            title = originScratchpad.get("translatedTitle");
        }

        return (
            <div>
                <Trans>Based on: {title}</Trans>
            </div>
        );
    }

    getNickname(): React.ReactNode {
        const {creatorProfile, scratchpad, profile} = this.props;

        const dataUserKaid = scratchpad.get("kaid");
        if (!creatorProfile || !creatorProfile.get("nickname")) {
            return <View data-user-kaid={dataUserKaid}>{t`New visitor`}</View>;
        }

        const nicknameText = creatorProfile.get("nickname");
        const currentUserIsModerator = profile && profile.get("isModerator");

        return creatorProfile.get("isPublic") || currentUserIsModerator ? (
            // TODO(WEB-1398): Replace HoverCard with DiscussionHoverCard
            <Link
                href={creatorProfile.get("profileRoot")}
                data-user-kaid={dataUserKaid}
            >
                {nicknameText}
            </Link>
        ) : (
            <View data-user-kaid={dataUserKaid} style={styles.nicknameNoLink}>
                {nicknameText}
            </View>
        );
    }

    /**
     * For officially published programs, we use the authorList to render the
     * author details.
     */
    renderPublishedContentAuthor(): React.ReactNode {
        const {authorList} = this.props;
        return authorList ? <AuthorList authorList={authorList} /> : null;
    }

    /**
     * For custom programs, we use the creatorProfile to render author
     * information.
     */
    renderCustomProgramAuthor(): React.ReactNode {
        const {creatorProfile} = this.props;
        if (!creatorProfile) {
            return null;
        }

        const nickname = this.getNickname();

        const {TimeAgo} = this.state;
        if (TimeAgo) {
            const lastUpdatedTime = (
                <TimeAgo time={this.props.lastUpdatedTime} />
            );

            const lastUpdated = (
                <span className={css(styles.lastUpdated)}>
                    (<Trans>Updated {lastUpdatedTime}</Trans>)
                </span>
            );

            return (
                <div>
                    <Trans>Created by: {nickname}</Trans>
                    {!this.props.embedded &&
                        this.props.lastUpdatedTime &&
                        lastUpdated}
                </div>
            );
        }
        return (
            <div>
                <Trans>Created by: {nickname}</Trans>
            </div>
        );
    }

    renderCreatorDetails(): React.ReactNode {
        const {creatorProfile, scratchpad} = this.props;
        // TODO(LP-12559): We have a fallback condition to continue supporting
        // legacy Program content pages that still use creatorProfile to render
        // author details. Once 321 is launched for CS pages we don't need the
        // "|| creatorProfile" fallback anymore so remove it.
        if (scratchpad.get("contentKind") === "Scratchpad" || creatorProfile) {
            return this.renderCustomProgramAuthor();
        }

        return this.renderPublishedContentAuthor();
    }

    render(): React.ReactNode {
        return (
            <div className={css(styles.about)}>
                {this.renderDescription()}
                {this.renderButtons()}
                {this.renderSpinOffParentDetails()}
                {this.renderCreatorDetails()}
            </div>
        );
    }
}

const styles = StyleSheet.create({
    about: {
        ...deprecatedTypography.bodySmall,
        color: color.offBlack,
        textAlign: "center",
    },

    description: {
        marginBottom: 22,
        textAlign: "left",
    },

    lastUpdated: {
        marginLeft: 4,
    },

    nicknameNoLink: {
        display: "inline-block",
    },
});

type ExportProps = WithoutUserProfile<
    JSX.LibraryManagedAttributes<
        typeof ScratchpadAbout,
        React.ComponentProps<typeof ScratchpadAbout>
    >
>;

export default compose(
    withBackboneModels((props: Props) => {
        const models = [props.scratchpad];

        if (props.originScratchpad) {
            models.push(props.originScratchpad);
        }

        if (props.creatorProfile) {
            models.push(props.creatorProfile);
        }

        return models;
    }),
    withUserProfile,
)(ScratchpadAbout) as React.ComponentType<ExportProps>;
